import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux-tool-kit/Hooks";
import { Form, Formik } from "formik";
import { Row, Col, NavItem, NavLink, Nav } from "reactstrap";
import * as Yup from "yup";
import OrganizationInfoSection from "./OrganizationInfoSection";
import {
  OrganizationFormData,
  OrganizationListTableProduct,
} from "../../../types/organization/OrganizationTypes";
import { setCreatedData } from "../../../redux-tool-kit/reducers/ProjectSlice";
import { Href } from "../../../utils/Constant";
import { useTranslation } from "react-i18next";
import GeneralSettingsSection from "./GeneralSettingsSection";

import WhileLabelSection from "./WhiteLabel";
import BrandingSection from "./BrandingSection";

// Validation Schema for Organization Form
const validationSchema = Yup.object().shape({
  organizationName: Yup.string().required("Organization name is required"),
  industry: Yup.string().required("Category is required"),
  country: Yup.string().required("Country is required"),
  city: Yup.string().required("City is required"),
  timezone: Yup.string().required("Timezone is required"),
  currency: Yup.string().required("Currency is required"),
  status: Yup.string().required("Status is required"),
  contactPerson: Yup.string().required("Contact Person is required"),
  franchise: Yup.string().required("Franchise is required"),
  distributor: Yup.string().required("Distributor is required"),
  organizationWebsite: Yup.string().required(
    "Organization Website is required"
  ),
  language: Yup.string().required("Language is required"),
});

const CreateOrganization = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { createdFormData } = useAppSelector((state) => state.project);
  const [basicTab, setBasicTab] = useState<string>("1");

  // Find organization data if in edit mode
  const organizationData = id
    ? (createdFormData.find((item) => item.id?.toString() === id) as
        | OrganizationListTableProduct
        | undefined)
    : null;
  console.log(organizationData, "organizationData");

  const initialValues: OrganizationFormData = {
    organizationName: organizationData?.name || "",
    industry: organizationData?.industry || "",
    country: organizationData?.country || "",
    city: organizationData?.city || "",
    timezone: organizationData?.timezone || "",
    currency: organizationData?.currency || "",
    status: organizationData?.status || "Active",
    logo: "",
    mobileNumber: organizationData?.mobileNumber || "",
    contactPerson: organizationData?.contactPerson || "",
    address: organizationData?.address || "",
    email: organizationData?.email || "",
    accountType: organizationData?.accountType || "",
    PageLimit: organizationData?.PageLimit || "10",
    authority: organizationData?.authority || "",
    GoogleKey: organizationData?.GoogleKey || "",
    androidKey: organizationData?.androidKey || "",
    androidURL: organizationData?.androidURL || "",
    iosKey: organizationData?.iosKey || "",
    iosURL: organizationData?.iosURL || "",
    distributor: organizationData?.distributor || "",
    franchise: organizationData?.franchise || "",
    subscriptionType: organizationData?.subscriptionType || "",
    language: organizationData?.language || "English",
    dateFormat: organizationData?.dateFormat || "DD/MM/YYYY",
    languages:
      organizationData?.languages && organizationData.languages.length > 0
        ? organizationData.languages
        : ["English"],
  };

  const handleSubmit = (values: OrganizationFormData) => {
    const submittedData: OrganizationListTableProduct = {
      id:
        organizationData?.id || Math.floor(Math.random() * (100 - 10 + 1)) + 10,
      name: values.organizationName,
      industry: values.industry,
      country: values.country,
      city: values.city,
      timezone: values.timezone,
      currency: values.currency,
      entity: organizationData?.entity || 0,
      users: organizationData?.users || 0,
      status: values.status,
      image: organizationData?.image || "dashboard-8/linnk_logo.png",
      PageLimit: values.PageLimit,
      GoogleKey: values.GoogleKey,
      androidKey: values.androidKey,
      distributor: values.distributor,
      franchise: values.franchise,
      language: values.language,
    };

    if (organizationData) {
      // Update existing organization
      const updatedData = createdFormData.map((item) =>
        item.id === organizationData.id ? submittedData : item
      );
      dispatch(setCreatedData(updatedData));
    } else {
      // Create new organization
      dispatch(setCreatedData([...createdFormData, submittedData]));
    }

    navigate(`${process.env.PUBLIC_URL}/masterdata/organization`);
  };

  // Redirect if organization not found in edit mode
  useEffect(() => {
    if (id && !organizationData) {
      navigate(`${process.env.PUBLIC_URL}/masterdata/organization`);
    }
  }, [id, organizationData, navigate]);

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {() => (
          <Form className="theme-form">
            <Row>
              <Col>
                {id && (
                  <Nav tabs className="border-tab border-0 mb-0 nav-warning">
                    <NavItem>
                      <NavLink
                        href={Href}
                        className={`nav-border pt-0 txt-warning nav-warning ${
                          basicTab === "1" ? "active" : ""
                        }`}
                        onClick={() => setBasicTab("1")}
                      >
                        <i className="icofont icofont-man-in-glasses"></i>
                        {t("EditOrganization")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        href={Href}
                        className={`nav-border txt-warning nav-warning ${
                          basicTab === "2" ? "active" : ""
                        }`}
                        onClick={() => setBasicTab("2")}
                      >
                        <i className="icofont icofont-file-document"></i>
                        {t("WhiteLabel")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        href={Href}
                        className={`nav-border txt-warning nav-warning ${
                          basicTab === "3" ? "active" : ""
                        }`}
                        onClick={() => setBasicTab("3")}
                      >
                        <i className="icofont icofont-settings-alt"></i>
                        {t("Settings")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        href={Href}
                        className={`nav-border txt-warning nav-warning ${
                          basicTab === "4" ? "active" : ""
                        }`}
                        onClick={() => setBasicTab("4")}
                      >
                        <i className="icofont icofont-color-picker"></i>
                        {t("Branding")}
                      </NavLink>
                    </NavItem>
                  </Nav>
                )}
                {basicTab === "1" && (
                  <OrganizationInfoSection isEdit={!!organizationData} />
                )}
                 {basicTab === "3" && (
        <GeneralSettingsSection   />
      )}
     
              </Col>
            </Row>
          </Form>
        )}
      </Formik>

      {basicTab === "2" && <WhileLabelSection />}
      {basicTab === "4" && <BrandingSection />}
      
      
    </>
  );
};

export default CreateOrganization;
